import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../components/icon';
import SelectField from '../components/select-field';
import TextField from '../components/text-field';
import SectionTabs from '../components/section-tabs';
import ViewColumnsPanel from '../components/view-columns-panel';
import {
  createManagerViewPreset,
  deleteManagerViewPreset,
  getManagerTableState,
  listDivisions,
  listManagerViewPresets,
  listManagers,
  putManagerTableState,
  updateManagerViewPreset,
} from '../api/panel-api';
import ManagerDivisions from './manager-divisions';
import {
  COLUMN_META,
  columnsKey,
  defaultColumns,
  normalizeColumns,
  paneWidth,
  splitColumns,
} from '../data/manager-columns';
import { formatPhone, serviceRoleLabel } from '../data/panel';
import { managersPath, parseManagersLocation } from '../section-url';

const emptyFilters = {
  firstName: '',
  lastName: '',
  phone: '',
  email: '',
  divisionId: '',
  status: '',
  csRole: '',
};

const OUTSIDE_DIVISION = '__none__';

const statusOptions = [
  { value: 'working', label: 'Активные' },
  { value: 'dismissed', label: 'Уволенные' },
];

const csRoleOptions = [
  { value: 'rks', label: serviceRoleLabel.rks },
  { value: 'rd', label: serviceRoleLabel.rd },
  { value: 'manager', label: serviceRoleLabel.manager },
  { value: 'consultant', label: serviceRoleLabel.consultant },
];

function filtersFilled(filters) {
  return Boolean(
    filters.firstName
    || filters.lastName
    || filters.phone
    || filters.email
    || filters.divisionId
    || filters.status
    || filters.csRole,
  );
}

function toListFilters(filters, canFilterRole) {
  const body = {};
  const name = [filters.firstName, filters.lastName]
    .map((part) => String(part || '').trim())
    .filter(Boolean)
    .join(' ');
  if (name) body.name = name;
  if (filters.phone) body.phone = filters.phone;
  if (filters.email) body.email = filters.email;
  if (filters.divisionId === OUTSIDE_DIVISION) body.division_id = null;
  else if (filters.divisionId) body.division_id = filters.divisionId;
  if (filters.status) body.status = filters.status;
  if (canFilterRole && filters.csRole) body.cs_role = [filters.csRole];
  return body;
}

function filtersFromApi(filters) {
  const source = filters || {};
  const name = String(source.name || '').trim();
  const parts = name.split(/\s+/).filter(Boolean);
  return {
    firstName: parts.length > 1 ? parts[0] : '',
    lastName: parts.length > 1 ? parts.slice(1).join(' ') : name,
    phone: source.phone || '',
    email: source.email || '',
    divisionId: source.division_id === null ? OUTSIDE_DIVISION : (source.division_id || ''),
    status: source.status || '',
    csRole: Array.isArray(source.cs_role) && source.cs_role[0] ? source.cs_role[0] : '',
  };
}

const ROMAN = ['I', 'II', 'III', 'IV'];

function moscowParts(date = new Date()) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Moscow',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const value = (type) => Number(parts.find((part) => part.type === type).value);
  return { year: value('year'), month: value('month') };
}

function quarterStartFromMonth(year, month) {
  const q = Math.floor((month - 1) / 3);
  return `${year}-${String(q * 3 + 1).padStart(2, '0')}-01`;
}

function shiftQuarter(start, delta) {
  const year = Number(start.slice(0, 4));
  const month = Number(start.slice(5, 7));
  const index = year * 12 + (month - 1) + delta * 3;
  const nextYear = Math.floor(index / 12);
  const nextMonth = index - nextYear * 12;
  return `${nextYear}-${String(nextMonth + 1).padStart(2, '0')}-01`;
}

function currentQuarterStart() {
  const { year, month } = moscowParts();
  return quarterStartFromMonth(year, month);
}

function defaultQuarterStart() {
  return shiftQuarter(currentQuarterStart(), -1);
}

function completedQuarterOptions(count = 5) {
  const items = [];
  let start = shiftQuarter(currentQuarterStart(), -1);
  for (let i = 0; i < count; i += 1) {
    items.push({ start, label: quarterLongLabel(start) });
    start = shiftQuarter(start, -1);
  }
  return items;
}

function quarterIndex(start) {
  return Math.floor((Number(String(start).slice(5, 7)) - 1) / 3);
}

function quarterShortLabel(start) {
  if (!start) return '';
  return `${ROMAN[quarterIndex(start)]} кв`;
}

function quarterLongLabel(start) {
  if (!start) return '';
  return `${ROMAN[quarterIndex(start)]} квартал ${String(start).slice(0, 4)}`;
}

function formatAmount(value) {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return Math.round(n).toLocaleString('ru-RU').replace(/\u00A0/g, ' ');
}

function clientsCountText(item) {
  if (item.status === 'dismissed' || item.cs_role === 'consultant') return '—';
  return formatAmount(item.clients_count);
}

function pairValues(item, key) {
  if (key === 'orders_via_manager') {
    return {
      selected: item.orders_via_manager && item.orders_via_manager.selected,
      current: item.orders_via_manager && item.orders_via_manager.current,
    };
  }
  if (key === 'comments') {
    return {
      selected: item.comments && item.comments.selected,
      current: item.comments && item.comments.current,
    };
  }
  const selected = item.metrics && item.metrics.selected;
  const current = item.metrics && item.metrics.current;
  return {
    selected: selected && selected[key],
    current: current && current[key],
  };
}

function managerName(item) {
  return `${item.last_name} ${item.first_name}`.trim();
}

function presetKey(preset) {
  return preset && preset.id ? preset.id : 'default';
}

function ManagersPage({ sectionCode, location, role, onLocation }) {
  const [staffTotal, setStaffTotal] = useState(null);
  const [divisionTotal, setDivisionTotal] = useState(null);
  const [canSeeDivisions, setCanSeeDivisions] = useState(false);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(30);
  const [sort, setSort] = useState('last_name');
  const [order, setOrder] = useState('asc');
  const [quarterStart, setQuarterStart] = useState(defaultQuarterStart);
  const [columns, setColumns] = useState(defaultColumns);
  const [matchedView, setMatchedView] = useState(null);
  const [stateReady, setStateReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [quarterOpen, setQuarterOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [draft, setDraft] = useState(emptyFilters);
  const [applied, setApplied] = useState(emptyFilters);
  const [divisionOptions, setDivisionOptions] = useState([]);
  const [hoveredId, setHoveredId] = useState(null);
  const [viewEditor, setViewEditor] = useState(null);
  const [presetsOpen, setPresetsOpen] = useState(false);
  const [presets, setPresets] = useState([]);
  const [presetPick, setPresetPick] = useState('default');
  const [dialog, setDialog] = useState(null);
  const [presetName, setPresetName] = useState('');
  const [nameError, setNameError] = useState('');
  const [panelError, setPanelError] = useState('');
  const [pending, setPending] = useState(false);
  const menuRef = useRef(null);
  const pinnedRef = useRef(null);
  const scrollRef = useRef(null);
  const syncingScroll = useRef(false);
  const view = location.view === 'divisions' && canSeeDivisions ? 'divisions' : 'employees';
  const canCreateDivision = canSeeDivisions && (role === 'administrator' || role === 'cs_employee');
  const canFilterRole = role === 'administrator' || (role === 'cs_employee' && canSeeDivisions);
  const appliedCount = Object.values(applied).filter(Boolean).length;
  const draftFilled = filtersFilled(draft);
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  const selectedLabel = quarterShortLabel(quarterStart);
  const quarterOptions = completedQuarterOptions();
  const panes = splitColumns(columns);
  const pinnedWidth = Math.max(230, paneWidth(panes.pinned));
  const editorDirty = viewEditor ? columnsKey(viewEditor.columns) !== columnsKey(viewEditor.baseline) : false;

  function listBody() {
    return { ...toListFilters(applied, canFilterRole), quarter_start: quarterStart };
  }

  function writeState(next) {
    const body = {
      columns: next.columns || columns,
      sort: next.sort || sort,
      order: next.order || order,
      filters: toListFilters(next.filters || applied, canFilterRole),
      quarter_start: next.quarterStart !== undefined ? next.quarterStart : quarterStart,
    };
    return putManagerTableState(body).then((data) => {
      applyTableState(data, { keepPage: next.keepPage });
      return data;
    });
  }

  function applyTableState(data, { keepPage } = {}) {
    if (!data) return;
    if (data.columns) setColumns(normalizeColumns(data.columns));
    if (data.sort) setSort(data.sort);
    if (data.order) setOrder(data.order);
    if (data.filters) setApplied(filtersFromApi(data.filters));
    if (Object.prototype.hasOwnProperty.call(data, 'quarter_start')) {
      setQuarterStart(data.quarter_start || currentQuarterStart());
    }
    setMatchedView(data.matched_view_preset || null);
    if (!keepPage) setPage(1);
  }

  useEffect(() => {
    let cancelled = false;
    getManagerTableState()
      .then((data) => {
        if (cancelled) return;
        applyTableState(data, { keepPage: true });
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setStateReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    listDivisions()
      .then((data) => {
        if (cancelled) return;
        setCanSeeDivisions(true);
        setDivisionTotal(data.total);
        setDivisionOptions(data.items || []);
      })
      .catch((loadError) => {
        if (cancelled) return;
        setCanSeeDivisions(false);
        setDivisionTotal(null);
        setDivisionOptions([]);
        if (
          loadError.status === 403
          && parseManagersLocation(window.location.pathname, sectionCode).view === 'divisions'
        ) {
          onLocation({ view: 'employees' });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [sectionCode]);

  useEffect(() => {
    if (view !== 'employees' || !stateReady) return undefined;
    let cancelled = false;
    setLoading(true);
    listManagers({
      page,
      perPage: pageSize,
      sort,
      order,
      filters: listBody(),
    })
      .then((data) => {
        if (cancelled) return;
        const rows = data.items || [];
        setItems(rows);
        setTotal(data.total || 0);
        setStaffTotal(data.total);
        setError('');
        const fromRow = rows[0] && rows[0].metrics && rows[0].metrics.quarter_start;
        const normalized = fromRow ? String(fromRow).slice(0, 10) : '';
        if (normalized && normalized !== quarterStart) setQuarterStart(normalized);
      })
      .catch((loadError) => {
        if (cancelled) return;
        setItems([]);
        setError(loadError.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [view, stateReady, page, pageSize, sort, order, quarterStart, applied, canFilterRole]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    function onPointer(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
        setQuarterOpen(false);
      }
    }
    function onKey(event) {
      if (event.key === 'Escape') {
        setMenuOpen(false);
        setQuarterOpen(false);
      }
    }
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  function changeSort(field) {
    const nextOrder = sort === field && order === 'asc' ? 'desc' : 'asc';
    const nextSort = field;
    setSort(nextSort);
    setOrder(nextOrder);
    setPage(1);
    writeState({ sort: nextSort, order: nextOrder, keepPage: false }).catch(() => {});
  }

  function openCard(id) {
    onLocation({ view: 'card', managerId: id });
  }

  function openFilters() {
    setDraft(applied);
    setFiltersOpen(true);
  }

  function closeFilters() {
    setDraft(applied);
    setFiltersOpen(false);
  }

  function applyFilters() {
    setApplied(draft);
    setPage(1);
    setFiltersOpen(false);
    writeState({ filters: draft, keepPage: false }).catch(() => {});
  }

  function resetDraft() {
    setDraft(emptyFilters);
  }

  function resetAppliedFilters() {
    setDraft(emptyFilters);
    setApplied(emptyFilters);
    setPage(1);
    writeState({ filters: emptyFilters, keepPage: false }).catch(() => {});
  }

  function openCurrentView() {
    setMenuOpen(false);
    setPresetsOpen(false);
    setPanelError('');
    setViewEditor({
      mode: 'current',
      title: 'Настройка текущего вида',
      columns: normalizeColumns(columns),
      baseline: normalizeColumns(columns),
    });
  }

  function loadPresets() {
    return listManagerViewPresets().then((data) => {
      const list = Array.isArray(data) ? data : [];
      setPresets(list);
      return list;
    });
  }

  function openPresets() {
    setMenuOpen(false);
    setViewEditor(null);
    setPanelError('');
    setPresetsOpen(true);
    loadPresets()
      .then(() => {
        setPresetPick(matchedView && matchedView.id ? matchedView.id : 'default');
      })
      .catch((loadError) => setPanelError(loadError.message));
  }

  function closeViewEditor(force) {
    if (!force && editorDirty) {
      setDialog({ type: 'leave' });
      return;
    }
    setViewEditor(null);
    setPanelError('');
  }

  function applyViewColumns() {
    if (!viewEditor) return;
    setPending(true);
    writeState({ columns: viewEditor.columns, keepPage: true })
      .then(() => {
        setViewEditor(null);
        setPanelError('');
      })
      .catch((saveError) => setPanelError(saveError.message))
      .finally(() => setPending(false));
  }

  function applyPickedPreset() {
    const preset = presets.find((item) => presetKey(item) === presetPick) || presets[0];
    if (!preset) return;
    setPending(true);
    writeState({ columns: preset.columns, keepPage: true })
      .then(() => {
        setPresetsOpen(false);
        setPanelError('');
      })
      .catch((saveError) => setPanelError(saveError.message))
      .finally(() => setPending(false));
  }

  function openCreateView() {
    setPresetsOpen(false);
    setPanelError('');
    setViewEditor({
      mode: 'create',
      title: 'Новый вид',
      showBack: true,
      columns: defaultColumns(),
      baseline: defaultColumns(),
    });
  }

  function openEditPreset(preset) {
    setPresetsOpen(false);
    setPanelError('');
    setViewEditor({
      mode: 'edit',
      title: preset.name,
      showBack: true,
      preset,
      columns: normalizeColumns(preset.columns),
      baseline: normalizeColumns(preset.columns),
    });
  }

  function openNameDialog() {
    setPresetName('');
    setNameError('');
    loadPresets().catch(() => {});
    setDialog({ type: 'name' });
  }

  function takenName(name) {
    const needle = name.trim().toLowerCase();
    if (needle === 'по умолчанию') return true;
    return presets.some((item) => String(item.name || '').toLowerCase() === needle);
  }

  function saveNamedPreset(applyAfter) {
    const name = presetName.trim();
    if (!name) {
      setNameError('Укажите название');
      return;
    }
    if (takenName(name)) {
      setNameError('Имя уже занято');
      return;
    }
    if (!viewEditor) return;
    setPending(true);
    createManagerViewPreset({ name, columns: viewEditor.columns })
      .then((created) => {
        setDialog(null);
        setPresets((list) => list.concat(created));
        if (applyAfter) {
          return writeState({ columns: viewEditor.columns, keepPage: true }).then(() => {
            setViewEditor(null);
          });
        }
        if (viewEditor.mode === 'create') setViewEditor(null);
        return null;
      })
      .catch((saveError) => {
        setNameError((saveError.fields && saveError.fields.name) || saveError.message);
      })
      .finally(() => setPending(false));
  }

  function saveEditedPreset() {
    if (!viewEditor || !viewEditor.preset || !viewEditor.preset.id) return;
    setPending(true);
    updateManagerViewPreset(viewEditor.preset.id, {
      name: viewEditor.preset.name,
      columns: viewEditor.columns,
    })
      .then(() => {
        setViewEditor(null);
        setPanelError('');
      })
      .catch((saveError) => setPanelError(saveError.message))
      .finally(() => setPending(false));
  }

  function confirmDelete(preset) {
    setDialog({ type: 'delete', preset });
  }

  function deletePicked() {
    const preset = dialog && dialog.preset;
    if (!preset || !preset.id) return;
    setPending(true);
    deleteManagerViewPreset(preset.id)
      .then(() => {
        setPresets((list) => list.filter((item) => item.id !== preset.id));
        if (presetPick === preset.id) setPresetPick('default');
        if (matchedView && matchedView.id === preset.id) setMatchedView(null);
        setDialog(null);
      })
      .catch((saveError) => setPanelError(saveError.message))
      .finally(() => setPending(false));
  }

  function syncTableScroll(source) {
    return (event) => {
      if (syncingScroll.current) return;
      const other = source === 'pinned' ? scrollRef.current : pinnedRef.current;
      if (!other) return;
      syncingScroll.current = true;
      other.scrollTop = event.currentTarget.scrollTop;
      syncingScroll.current = false;
    };
  }

  function rowClass(id) {
    return hoveredId === id ? 'table__row is-hot' : 'table__row';
  }

  function rowPointer(id) {
    return {
      onMouseEnter: () => setHoveredId(id),
      onMouseLeave: () => setHoveredId(null),
      onClick: () => openCard(id),
      onKeyDown: (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          openCard(id);
        }
      },
    };
  }

  const tabs = [
    {
      id: 'employees',
      label: 'Сотрудники',
      href: managersPath(sectionCode, { view: 'employees' }),
      count: staffTotal,
    },
  ];
  if (canSeeDivisions) {
    tabs.push({
      id: 'divisions',
      label: 'Дивизионы',
      href: managersPath(sectionCode, { view: 'divisions' }),
      count: divisionTotal,
    });
  }

  return (
    <section className="content content--panel" aria-label="Менеджеры" aria-busy={loading}>
      <div className="content__scroll">
        <header className="page-head">
          <div className="page-head__title">
            <h1 className="page-title">Менеджеры</h1>
            <SectionTabs
              items={tabs}
              value={view}
              onChange={(id) => onLocation({ view: id })}
            />
          </div>
          {view === 'divisions' && canCreateDivision ? (
            <div className="page-actions">
              <a
                href={managersPath(sectionCode, { view: 'divisions', create: true })}
                className="btn"
                onClick={(event) => {
                  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
                  event.preventDefault();
                  onLocation({ view: 'divisions', create: true });
                }}
              >
                <Icon name="plus" size={16} tone="inherit" />
                Создать дивизион
              </a>
            </div>
          ) : null}
          {view === 'employees' ? (
            <div className="page-actions" ref={menuRef}>
              <button
                type="button"
                className="btn"
                aria-pressed={filtersOpen}
                onClick={openFilters}
              >
                <Icon name="filter" size={16} tone="inherit" />
                Фильтры
                {appliedCount ? <span className="btn__count">{appliedCount}</span> : null}
              </button>
              <button
                type="button"
                className={menuOpen ? 'icon-button icon-button--sheet is-pressed' : 'icon-button icon-button--sheet'}
                aria-label="Действия раздела"
                aria-expanded={menuOpen}
                aria-haspopup="menu"
                onClick={() => {
                  setMenuOpen((open) => !open);
                  setQuarterOpen(false);
                }}
              >
                <span className="kebab" aria-hidden="true"><i /><i /><i /></span>
              </button>
              {menuOpen ? (
                <div className="row-menu section-menu" role="menu">
                  <button type="button" className="menu-row" role="menuitem" onClick={openCurrentView}>
                    <Icon name="view-list" size={16} />
                    Настройка текущего вида
                  </button>
                  <button type="button" className="menu-row menu-row--stack" role="menuitem" onClick={openPresets}>
                    <Icon name="view-preset" size={16} />
                    <span className="menu-row__text">
                      Пресеты видов
                      {matchedView && matchedView.name ? <small>{matchedView.name}</small> : null}
                    </span>
                  </button>
                  <div
                    className="section-menu__item"
                    onMouseEnter={() => setQuarterOpen(true)}
                    onMouseLeave={() => setQuarterOpen(false)}
                  >
                    <button
                      type="button"
                      className="menu-row"
                      role="menuitem"
                      aria-haspopup="true"
                      aria-expanded={quarterOpen}
                      onClick={() => setQuarterOpen((open) => !open)}
                    >
                      <Icon name="calendar" size={16} />
                      Квартал показателей
                      <Icon name="chevron-right" size={16} />
                    </button>
                    {quarterOpen ? (
                      <div className="row-menu section-menu section-menu--nested" role="menu">
                        {quarterOptions.map((option) => (
                          <button
                            key={option.start}
                            type="button"
                            className={option.start === quarterStart ? 'menu-row is-selected' : 'menu-row'}
                            role="menuitem"
                            onClick={() => {
                              setQuarterStart(option.start);
                              setPage(1);
                              setMenuOpen(false);
                              setQuarterOpen(false);
                              writeState({ quarterStart: option.start, keepPage: false }).catch(() => {});
                            }}
                          >
                            {option.label}
                            {option.start === quarterStart ? <Icon name="check" size={16} /> : null}
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
        </header>
        {view === 'employees' ? (
          <>
            <div className="managers-table">
              <div
                className="managers-table__pinned"
                ref={pinnedRef}
                style={{ flex: `0 0 ${pinnedWidth}px`, width: pinnedWidth }}
                onScroll={syncTableScroll('pinned')}
              >
                <TablePane
                  columns={panes.pinned}
                  items={items}
                  sort={sort}
                  order={order}
                  selectedLabel={selectedLabel}
                  onSort={changeSort}
                  rowClass={rowClass}
                  rowPointer={rowPointer}
                />
              </div>
              <div className="managers-table__main">
                <div className="managers-table__viewport" ref={scrollRef} onScroll={syncTableScroll('scroll')}>
                  <TablePane
                    columns={panes.rest}
                    items={items}
                    sort={sort}
                    order={order}
                    selectedLabel={selectedLabel}
                    onSort={changeSort}
                    rowClass={rowClass}
                    rowPointer={rowPointer}
                  />
                </div>
                <div className="managers-table__edge managers-table__edge--right" aria-hidden="true" />
              </div>
              <div
                className="managers-table__edge managers-table__edge--left"
                style={{ left: pinnedWidth }}
                aria-hidden="true"
              />
              {!error && !loading && items.length === 0 ? (
                <div className="empty managers-empty">
                  <div className="empty__icon"><Icon name="filter" size={24} tone="inherit" /></div>
                  <div>
                    <p className="empty__title">Ничего не найдено</p>
                    <p className="empty__lead">Измените условия фильтров или сбросьте их.</p>
                  </div>
                  <button type="button" className="btn btn--secondary" onClick={resetAppliedFilters}>
                    Сбросить фильтры
                  </button>
                </div>
              ) : null}
            </div>
            {error ? (
              <div className="empty"><p>{error}</p></div>
            ) : null}
            {items.length ? (
              <footer className="pager">
                <label className="pager__size">
                  Показывать по
                  <select value={pageSize} onChange={(event) => { setPageSize(Number(event.target.value)); setPage(1); }}>
                    <option value={30}>30</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </label>
                <div className="pager__nav">
                  <span>{`Всего ${total}`}</span>
                  <button type="button" className="icon-button" aria-label="Предыдущая страница" disabled={page <= 1} onClick={() => setPage((value) => value - 1)}>
                    <Icon name="chevron-left" size={16} />
                  </button>
                  <span className="counter counter--page">{page}</span>
                  <button type="button" className="icon-button" aria-label="Следующая страница" disabled={page >= pageCount} onClick={() => setPage((value) => value + 1)}>
                    <Icon name="chevron-right" size={16} />
                  </button>
                </div>
              </footer>
            ) : null}
          </>
        ) : view === 'divisions' ? (
          <ManagerDivisions
            sectionCode={sectionCode}
            location={location}
            canWrite={canCreateDivision}
            onLocation={onLocation}
            onListChange={({ items: rows, total: count }) => {
              setDivisionTotal(count);
              setDivisionOptions(rows);
            }}
          />
        ) : null}
      </div>
      {filtersOpen ? createPortal(
        <>
          <button type="button" className="filter-shade" aria-label="Закрыть фильтры" onClick={closeFilters} />
          <aside className="filter-panel" aria-label="Фильтры">
            <div className="filter-panel__head">
              <div className="filter-panel__title">
                <h2>Фильтры</h2>
                <button
                  type="button"
                  className="btn btn--text btn--small"
                  disabled={!draftFilled}
                  onClick={resetDraft}
                >
                  Сбросить
                </button>
              </div>
              <button type="button" className="icon-button" aria-label="Закрыть" onClick={closeFilters}>
                <Icon name="x" size={18} />
              </button>
            </div>
            <div className="filter-panel__body">
              <TextField
                label="Имя"
                value={draft.firstName}
                required={false}
                trailing="clear"
                onChange={(value) => setDraft({ ...draft, firstName: value })}
              />
              <TextField
                label="Фамилия"
                value={draft.lastName}
                required={false}
                trailing="clear"
                onChange={(value) => setDraft({ ...draft, lastName: value })}
              />
              <TextField
                label="Телефон"
                value={draft.phone}
                required={false}
                trailing="clear"
                autoComplete="off"
                onChange={(value) => setDraft({ ...draft, phone: formatPhone(value) })}
              />
              <TextField
                label="Электронная почта"
                type="email"
                value={draft.email}
                required={false}
                trailing="clear"
                autoComplete="off"
                onChange={(value) => setDraft({ ...draft, email: value })}
              />
              <SelectField
                label="Дивизион"
                required={false}
                clearable
                value={draft.divisionId}
                onChange={(value) => setDraft({ ...draft, divisionId: value })}
                options={[
                  { value: OUTSIDE_DIVISION, label: 'Вне дивизиона' },
                  ...divisionOptions.map((item) => ({ value: item.id, label: item.title })),
                ]}
              />
              <SelectField
                label="Статус"
                required={false}
                clearable
                value={draft.status}
                onChange={(value) => setDraft({ ...draft, status: value })}
                options={statusOptions}
              />
              {canFilterRole ? (
                <SelectField
                  label="Роль в клиентской службе"
                  required={false}
                  clearable
                  value={draft.csRole}
                  onChange={(value) => setDraft({ ...draft, csRole: value })}
                  options={csRoleOptions}
                />
              ) : null}
            </div>
            <div className="filter-panel__foot">
              <button type="button" className="btn" onClick={applyFilters}>
                Применить
              </button>
            </div>
          </aside>
        </>,
        document.body,
      ) : null}
      {viewEditor ? createPortal(
        <>
          <button type="button" className="filter-shade" aria-label="Закрыть" onClick={() => closeViewEditor(false)} />
          <ViewColumnsPanel
            title={viewEditor.title}
            showBack={viewEditor.showBack}
            columns={viewEditor.columns}
            dirty={editorDirty}
            pending={pending}
            error={panelError}
            mode={viewEditor.mode}
            onChange={(next) => setViewEditor((current) => ({ ...current, columns: next }))}
            onClose={() => closeViewEditor(false)}
            onBack={() => {
              if (editorDirty) {
                setDialog({ type: 'leave', back: true });
                return;
              }
              setViewEditor(null);
              setPresetsOpen(true);
            }}
            onApply={applyViewColumns}
            onSave={saveEditedPreset}
            onSavePreset={openNameDialog}
            onSaveAs={openNameDialog}
          />
        </>,
        document.body,
      ) : null}
      {presetsOpen && !viewEditor ? createPortal(
        <>
          <button type="button" className="filter-shade" aria-label="Закрыть" onClick={() => setPresetsOpen(false)} />
          <aside className="filter-panel filter-panel--view" aria-label="Пресеты видов">
            <div className="filter-panel__head">
              <div className="filter-panel__title">
                <h2>Пресеты видов</h2>
              </div>
              <button type="button" className="icon-button" aria-label="Закрыть" onClick={() => setPresetsOpen(false)}>
                <Icon name="x" size={18} />
              </button>
            </div>
            <div className="filter-panel__body view-presets">
              {presets.map((preset) => {
                const key = presetKey(preset);
                const selected = presetPick === key;
                return (
                  <div key={key} className={selected ? 'view-presets__row is-on' : 'view-presets__row'}>
                    <button type="button" className="view-presets__pick" onClick={() => setPresetPick(key)}>
                      <span className={selected ? 'radio is-on' : 'radio'} />
                      {preset.name}
                    </button>
                    {preset.editable !== false && preset.id ? (
                      <div className="view-presets__actions">
                        <button type="button" className="icon-button" aria-label="Редактировать" onClick={() => openEditPreset(preset)}>
                          <Icon name="pencil" size={18} />
                        </button>
                        <button type="button" className="icon-button" aria-label="Удалить" onClick={() => confirmDelete(preset)}>
                          <Icon name="trash" size={18} />
                        </button>
                      </div>
                    ) : null}
                  </div>
                );
              })}
              <div className="view-presets__create">
                <button type="button" className="btn btn--text btn--small" onClick={openCreateView}>
                  <Icon name="plus" size={16} tone="inherit" />
                  Создать вид
                </button>
              </div>
              {panelError ? <p className="empty">{panelError}</p> : null}
            </div>
            <div className="filter-panel__foot">
              <button type="button" className="btn" disabled={pending} onClick={applyPickedPreset}>
                Применить
              </button>
            </div>
          </aside>
        </>,
        document.body,
      ) : null}
      {dialog ? createPortal(
        <div className="overlay overlay--front">
          {dialog.type === 'leave' ? (
            <div className="dialog" role="dialog" aria-modal="true">
              <div className="dialog__head">
                <h2>Выйти без сохранения?</h2>
                <button type="button" className="icon-button" aria-label="Закрыть" onClick={() => setDialog(null)}>
                  <Icon name="x" size={18} />
                </button>
              </div>
              <div className="dialog__body">
                <div className="note note--warning">
                  <Icon name="alert" size={20} />
                  <p>Есть несохранённые или неприменённые изменения. Если выйти, они пропадут.</p>
                </div>
              </div>
              <div className="dialog__footer">
                <button type="button" className="btn btn--secondary btn--small" onClick={() => setDialog(null)}>Остаться</button>
                <button
                  type="button"
                  className="btn btn--danger btn--small"
                  onClick={() => {
                    const back = dialog.back;
                    setDialog(null);
                    setViewEditor(null);
                    if (back) setPresetsOpen(true);
                  }}
                >
                  Выйти
                </button>
              </div>
            </div>
          ) : null}
          {dialog.type === 'name' ? (
            <div className="dialog" role="dialog" aria-modal="true">
              <div className="dialog__head">
                <h2>Сохранить пресет вида</h2>
                <button type="button" className="icon-button" aria-label="Закрыть" onClick={() => setDialog(null)}>
                  <Icon name="x" size={18} />
                </button>
              </div>
              <div className="dialog__body">
                <TextField
                  label="Название пресета"
                  value={presetName}
                  error={nameError}
                  onChange={(value) => {
                    setPresetName(value);
                    setNameError('');
                  }}
                />
                <div className="note note--info">
                  <Icon name="info" size={20} />
                  <p>В пресет попадут колонки, их порядок и закрепление.</p>
                </div>
              </div>
              <div className="dialog__footer">
                <button
                  type="button"
                  className="btn btn--secondary btn--small"
                  disabled={pending || !presetName.trim()}
                  onClick={() => saveNamedPreset(false)}
                >
                  Сохранить
                </button>
                <button
                  type="button"
                  className="btn btn--small"
                  disabled={pending || !presetName.trim()}
                  onClick={() => saveNamedPreset(true)}
                >
                  Сохранить и применить
                </button>
              </div>
            </div>
          ) : null}
          {dialog.type === 'delete' ? (
            <div className="dialog" role="dialog" aria-modal="true">
              <div className="dialog__head">
                <h2><Icon name="trash" size={22} />Удалить пресет вида</h2>
                <button type="button" className="icon-button" aria-label="Закрыть" onClick={() => setDialog(null)}>
                  <Icon name="x" size={18} />
                </button>
              </div>
              <div className="dialog__body">
                <div className="note note--danger">
                  <Icon name="alert" size={20} />
                  <p>
                    Пресет «{dialog.preset.name}» удалится безвозвратно. Колонки в таблице не изменятся, пропадёт только подпись под пунктом меню.
                  </p>
                </div>
              </div>
              <div className="dialog__footer">
                <button type="button" className="btn btn--secondary btn--small" onClick={() => setDialog(null)}>Отмена</button>
                <button type="button" className="btn btn--danger btn--small" disabled={pending} onClick={deletePicked}>Удалить</button>
              </div>
            </div>
          ) : null}
        </div>,
        document.body,
      ) : null}
    </section>
  );
}

function SortHead({ label, field, sort, order, onSort }) {
  const active = sort === field;
  return (
    <button
      type="button"
      className={active && order === 'desc' ? 'sort is-desc' : 'sort'}
      onClick={() => onSort(field)}
    >
      {label}
      {active ? <Icon name="arrow-up" size={14} /> : null}
    </button>
  );
}

function MetricDelta({ selected, current }) {
  if (selected === null || selected === undefined || selected === '' || current === null || current === undefined || current === '') {
    return null;
  }
  const from = Number(selected);
  const to = Number(current);
  if (!Number.isFinite(from) || !Number.isFinite(to) || (from === 0 && to === 0)) return null;
  if (from === 0 && to > 0) {
    return <span className="metric-delta metric-delta--new">NEW</span>;
  }
  if (from === 0) return null;
  const percent = Math.round(Math.abs(((to - from) / from) * 100));
  if (to === from) {
    return <span className="metric-delta">{percent} %</span>;
  }
  const up = to > from;
  return (
    <span className={up ? 'metric-delta metric-delta--up' : 'metric-delta metric-delta--down'}>
      <Icon name="trend-up" size={12} tone="inherit" />
      {percent} %
    </span>
  );
}

function TablePane({ columns, items, sort, order, selectedLabel, onSort, rowClass, rowPointer }) {
  const flats = columns.filter((item) => !COLUMN_META[item.code].metric);
  const metrics = columns.filter((item) => COLUMN_META[item.code].metric);
  const lastFlat = flats[flats.length - 1];
  const tableClass = metrics.length ? 'table managers-table__metrics' : 'table managers-table__names';
  const lastFlatCode = metrics.length && lastFlat ? lastFlat.code : null;

  return (
    <table className={tableClass} style={{ width: metrics.length ? undefined : paneWidth(columns) }}>
      <thead>
        <tr>
          {flats.map((item) => {
            const meta = COLUMN_META[item.code];
            return (
              <th
                key={item.code}
                rowSpan={metrics.length ? 2 : 1}
                className={item.code === lastFlatCode ? 'managers-table__split' : undefined}
                style={{ width: meta.width }}
              >
                <SortHead label={meta.tableLabel} field={meta.sort} sort={sort} order={order} onSort={onSort} />
              </th>
            );
          })}
          {metrics.map((item) => {
            const meta = COLUMN_META[item.code];
            return (
              <th
                key={item.code}
                colSpan={2}
                className="managers-table__group"
                style={{ width: meta.selectedWidth + meta.currentWidth }}
              >
                {meta.tableLabel}
              </th>
            );
          })}
        </tr>
        {metrics.length ? (
          <tr>
            {metrics.flatMap((item) => {
              const meta = COLUMN_META[item.code];
              return [
                <th key={`${item.code}-selected`} className="managers-table__period" style={{ width: meta.selectedWidth }}>
                  <SortHead label={selectedLabel} field={meta.sort} sort={sort} order={order} onSort={onSort} />
                </th>,
                <th key={`${item.code}-current`} className="managers-table__period" style={{ width: meta.currentWidth }}>
                  <SortHead label="Текущий" field={meta.sort} sort={sort} order={order} onSort={onSort} />
                </th>,
              ];
            })}
          </tr>
        ) : null}
      </thead>
      <tbody>
        {items.map((row) => (
          <tr
            key={row.id}
            className={rowClass(row.id)}
            tabIndex={0}
            {...rowPointer(row.id)}
          >
            {columns.flatMap((item) => renderCells(row, item.code, item.code === lastFlatCode))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function renderCells(item, code, split) {
  const meta = COLUMN_META[code];
  const splitClass = split ? 'managers-table__split' : undefined;
  if (code === 'manager') {
    return [
      <td key={code} className={split ? 'table__name managers-table__split' : 'table__name'} title={managerName(item)}>{managerName(item)}</td>,
    ];
  }
  if (code === 'status') {
    return [
      <td key={code} className={splitClass}>
        <Icon name={item.status === 'dismissed' ? 'x' : 'status-active'} size={18} />
      </td>,
    ];
  }
  if (code === 'cs_role') {
    return [<td key={code} className={splitClass}>{serviceRoleLabel[item.cs_role] || item.cs_role}</td>];
  }
  if (code === 'division') {
    return [<td key={code} className={splitClass}>{item.division && item.division.title ? item.division.title : 'Вне дивизиона'}</td>];
  }
  if (code === 'clients_count') {
    return [<td key={code} className={splitClass}>{clientsCountText(item)}</td>];
  }
  if (meta.metric) {
    const values = pairValues(item, code);
    return [
      <td key={`${code}-selected`}>{formatAmount(values.selected)}</td>,
      <td key={`${code}-current`}>
        <span className="metric-cell">
          {formatAmount(values.current)}
          <MetricDelta selected={values.selected} current={values.current} />
        </span>
      </td>,
    ];
  }
  return [];
}

export default ManagersPage;
